/**
 * Guardian — Google Analytics (GA4) event helper
 *
 * Single point of entry for all custom analytics events so we can keep names
 * consistent across the app and rename in one place. The gtag base snippet
 * (Measurement ID: G-EGW0FRW1KE) is injected in /public/index.html and only
 * loads outside the Emergent visual-edit iframe.
 *
 * Usage:
 *   import { trackEvent, EVENTS } from '../lib/analytics';
 *   trackEvent(EVENTS.UPGRADE_CLICKED, { location: 'sidebar' });
 */

export const EVENTS = {
  // Acquisition funnel
  TEST_DRIVE_SIGNUP: 'test_drive_signup',
  SIGN_UP: 'sign_up',          // GA4 recommended name
  LOGIN: 'login',              // GA4 recommended name
  MOBILE_LOGIN: 'mobile_login',
  LOGOUT: 'logout',

  // Monetisation funnel
  UPGRADE_CLICKED: 'upgrade_clicked',
  PLAN_LIMIT_HIT: 'plan_limit_hit',
  PLAN_LIMIT_BANNER_VIEW: 'plan_limit_banner_view',

  // Engagement (high-value workflow milestones)
  ASSET_CREATED: 'asset_created',
  INSPECTION_COMPLETED: 'inspection_completed',
  WORK_ORDER_COMPLETED: 'work_order_completed',
  DEFECT_REPORTED: 'defect_reported',
};

/**
 * Path safe to send to GA: no query string or hash (reset/invite tokens, ?view=<id>), and any
 * segment that isn't a plain lowercase word (record IDs, unit codes, QR codes) becomes ":id".
 * e.g. /assets/3f2a…/edit -> /assets/:id/edit, /reset-password?token=… -> /reset-password
 */
export const safePagePath = (pathname = '/') =>
  '/' + pathname.split('/').filter(Boolean)
    .map((s) => (/^[a-z][a-z-]{0,39}$/.test(s) ? s : ':id'))
    .join('/');

export const safePageLocation = (pathname) =>
  (typeof window === 'undefined' ? '' : window.location.origin) + safePagePath(pathname);

/**
 * Fire a GA4 event. Silently no-ops if gtag isn't loaded (e.g. inside the
 * Emergent editor iframe or before the script has loaded).
 *
 * @param {string} eventName  — see EVENTS
 * @param {object} [params]   — optional structured params for GA4
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', eventName, params);
  } catch (err) {
    // Never let analytics break the app
    // eslint-disable-next-line no-console
    console.debug('[analytics] event failed', eventName, err);
  }
};

/**
 * Set a user property (e.g. plan tier) so events can be segmented in GA.
 * Pass null to clear.
 */
export const setUserProperty = (name, value) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag('set', 'user_properties', { [name]: value });
  } catch {
    /* no-op */
  }
};
